import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api-client';
import { formatDate } from '@/lib/utils';
import {
  ApiError,
  getErrorMessage,
  logApiError
} from '@/lib/errors';
import type { SubscriptionDetailed } from '@/api/generated/client/models/SubscriptionDetailed';
import type { CurrentUser } from '@/types/user';

export type BillingInterval = 'monthly' | 'yearly';
export type TierCode = 'free' | 'starter' | 'pro' | 'expert';
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled';

export interface BackendTier {
  code: TierCode;
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  creditRateCents: number;
}

export interface Tier extends BackendTier {
  name: string;
  displayName: string;
  description: string;
}

const TIER_METADATA: Record<TierCode, { name: string; displayName: string; description: string }> = {
  free: {
    name: 'Free',
    displayName: 'Free',
    description: 'Get started with basic features',
  },
  starter: {
    name: 'Starter',
    displayName: 'Starter',
    description: 'Perfect for small teams',
  },
  pro: {
    name: 'Professional',
    displayName: 'Professional',
    description: 'For growing businesses',
  },
  expert: {
    name: 'Expert',
    displayName: 'Expert',
    description: 'Advanced features for experts',
  },
};

export interface UseSubscriptionPlansOptions {
  redirectPath?: 'onboarding' | 'settings';
}

export function useSubscriptionPlans(options: UseSubscriptionPlansOptions = {}) {
  const { redirectPath = 'settings' } = options;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<TierCode | null>(null);
  const hasProcessedSuccess = useRef(false);
  const pollTimeoutIdRef = useRef<number | null>(null);
  const startPollTimeoutIdRef = useRef<number | null>(null);

  // Get current user
  const { data: user } = useQuery({
    queryKey: ['/api/users/me'],
    queryFn: async (): Promise<CurrentUser> => {
      const data = await apiClient.users.getCurrentUser();
      return data as unknown as CurrentUser;
    },
  });

  // Fetch available tiers from backend (includes Stripe pricing)
  const { data: backendTiers, isLoading: tiersLoading, error: tiersError } = useQuery({
    queryKey: ['/api/billing/tiers'],
    queryFn: async (): Promise<BackendTier[]> => {
      const data = await apiClient.billing.getBillingTiers();
      return data as BackendTier[];
    },
  });

  // Log errors for debugging
  useEffect(() => {
    if (tiersError) {
      toast({
        title: 'Error Loading Plans',
        description: getErrorMessage(tiersError),
        variant: 'destructive',
      });
    }
  }, [tiersError, toast]);

  // Enrich backend tiers with metadata
  const tiers: Tier[] = useMemo(() => {
    return backendTiers?.map(tier => ({
      ...tier,
      ...TIER_METADATA[tier.code],
    })) || [];
  }, [backendTiers]);

  // Fetch company subscription
  const { data: subscription, refetch: refetchSubscription } = useQuery({
    queryKey: [`/api/billing/subscriptions/company/${user?.companyId}`],
    queryFn: async (): Promise<SubscriptionDetailed | null> => {
      if (!user?.companyId) throw new ApiError('No company ID');
      try {
        const data = await apiClient.billing.getBillingSubscriptionByCompany({ companyId: user.companyId });
        return data;
      } catch (error: unknown) {
        const err = error as { response?: { status?: number }; status?: number };
        const status = err?.response?.status ?? err?.status;
        if (status === 404) return null;
        throw error;
      }
    },
    enabled: !!user?.companyId,
  });

  // Handle success/cancel from Stripe Checkout redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSuccess = params.get('success') === 'true';
    const isCanceled = params.get('canceled') === 'true';

    // Don't process until user is loaded or if already processed
    if (!user?.companyId || hasProcessedSuccess.current) return;

    if (isSuccess) {
      hasProcessedSuccess.current = true;

      // Clean up URL immediately to prevent re-processing
      window.history.replaceState({}, '', window.location.pathname + '?tab=plans');

      // Stripe webhooks may take a moment to process, so we'll poll for updates
      const maxPolls = 10;
      const pollInterval = 1000; // 1 second

      const pollForSubscription = async (attempt: number) => {

        // Refetch subscription data and wait for result
        const { data: subscriptionData } = await refetchSubscription();

        // If we have subscription data or reached max polls, stop and show toast
        if (subscriptionData || attempt >= maxPolls) {
          if (subscriptionData) {
            toast({
              title: 'Subscription Updated!',
              description: 'Your subscription has been successfully updated.',
            });
          } else {
            toast({
              title: 'Processing...',
              description: 'Your subscription is being activated. Please refresh if it doesn\'t appear shortly.',
            });
          }

          // Reset the flag so next success can be processed
          hasProcessedSuccess.current = false;
        } else {
          // Continue polling
          pollTimeoutIdRef.current = window.setTimeout(() => {
            void pollForSubscription(attempt + 1);
          }, pollInterval);
        }
      };

      // Start polling after a short delay to give webhook time to process
      startPollTimeoutIdRef.current = window.setTimeout(() => {
        void pollForSubscription(1);
      }, 500);

    } else if (isCanceled) {
      toast({
        title: 'Checkout Canceled',
        description: 'Your subscription was not changed.',
        variant: 'destructive',
      });

      // Clean up URL after processing
      window.history.replaceState({}, '', window.location.pathname + '?tab=plans');
    }

    return () => {
      if (startPollTimeoutIdRef.current !== null) {
        window.clearTimeout(startPollTimeoutIdRef.current);
        startPollTimeoutIdRef.current = null;
      }
      if (pollTimeoutIdRef.current !== null) {
        window.clearTimeout(pollTimeoutIdRef.current);
        pollTimeoutIdRef.current = null;
      }
    };
  }, [toast, user?.companyId, refetchSubscription]);

  // Fetch estimated credit usage for current billing period
  const { data: totalUsage } = useQuery({
    queryKey: [`/api/billing/credit-usage/company/${user?.companyId}/period`],
    queryFn: async (): Promise<{ total: number }> => {
      if (!user?.companyId) throw new ApiError('No company ID');
      
      const response = await apiClient.billing.getBillingCreditUsagePeriod({ 
        companyId: user.companyId 
      });
      
      return { total: response.total };
    },
    enabled: !!user?.companyId,
  });

  // Fetch remaining credit balance from Stripe credit grants
  const { data: creditBalance } = useQuery({
    queryKey: ['credit-balance', user?.companyId],
    queryFn: async () => {
      const response = await apiClient.billing.getBillingStripeCreditBalance();
      const balance = response.balance;
      if (balance?.availableBalanceCents == null || !balance?.currency) return null;
      return { availableBalanceCents: balance.availableBalanceCents, currency: balance.currency };
    },
    enabled: !!user?.companyId,
    staleTime: 60_000,
  });

  // Create/Update subscription mutation
  const subscriptionMutation = useMutation({
    mutationFn: async ({ tierCode, billingPeriod }: { tierCode: TierCode; billingPeriod: BillingInterval }) => {
      if (!user?.companyId) throw new ApiError('No company ID');

      // If user has an active or past_due subscription, use the change subscription endpoint
      // Canceled subscriptions should go through checkout flow to create a new subscription
      const hasActiveSubscription = subscription?.stripeSubscriptionId && 
        subscription?.status !== 'canceled';

      if (hasActiveSubscription) {
        // Upgrading to a paid tier requires a payment method on file. Only a
        // *free-tier* subscriber can have an active Stripe subscription without a
        // card; changing their subscription to a paid tier would create an
        // invoice that can't be charged. Paid tiers always have a card on file
        // (enforced at checkout), so we skip the extra Stripe status roundtrip
        // for paid->paid changes and only check on the free->paid upgrade.
        const upgradingFromFree = subscription?.tierCode === 'free';
        if (tierCode !== 'free' && upgradingFromFree) {
          const currentStatus = await apiClient.billing.getBillingStripeSubscriptionStatusFromStripe();
          if (!currentStatus.hasPaymentMethod) {
            const baseUrl = window.location.origin;
            const returnUrl = redirectPath === 'onboarding'
              ? `${baseUrl}/plans`
              : `${baseUrl}/settings?tab=plans`;
            const portal = await apiClient.billing.createBillingPortalSession({
              createBillingPortalSessionRequest: { returnUrl },
            });
            if (!portal.url) {
              throw new Error('No billing portal URL received');
            }
            window.location.href = portal.url;
            return { redirectingToPortal: true as const };
          }
        }

        const result = await apiClient.billing.changeBillingSubscription({
          changeBillingSubscriptionRequest: {
            tierCode,
            billingPeriod,
          }
        });
        return result;
      }

      // Free tier does not require a credit/debit card. Create the subscription
      // directly via the API (skipping Stripe Checkout entirely). A paywall is
      // enforced later when the user tries to run a comparison/supplement.
      if (tierCode === 'free') {
        const created = await apiClient.billing.createBillingSubscription({
          createSubscriptionRequest: {
            companyId: user.companyId,
            tierCode,
            billingPeriod,
          }
        });
        return { freeCreated: true as const, subscription: created };
      }

      // Otherwise, create new subscription via Stripe checkout
      const baseUrl = window.location.origin;

      // Determine redirect URLs based on context
      const redirectUrls = redirectPath === 'onboarding'
        ? {
          successUrl: `${baseUrl}/`,
          cancelUrl: `${baseUrl}/plans?canceled=true`,
        }
        : {
          successUrl: `${baseUrl}/`,
          cancelUrl: `${baseUrl}/settings?tab=plans&canceled=true`,
        };

      const result = await apiClient.billing.createBillingCheckoutSession({
        createBillingCheckoutSessionRequest: {
          tierCode,
          billingPeriod,
          ...redirectUrls,
        }
      });

      // Validate checkout URL
      if (!result.url) {
        throw new Error('No checkout URL received');
      }

      // For checkout sessions, redirect to Stripe
      window.location.href = result.url;

      return result;
    },
    onSuccess: (data) => {
      // Redirecting to the Stripe billing portal to add a payment method before
      // completing a paid upgrade — navigation is already in progress, nothing to do.
      if ('redirectingToPortal' in data && data.redirectingToPortal) {
        return;
      }

      // Handle direct free-tier subscription creation (no Stripe checkout, no card)
      if ('freeCreated' in data && data.freeCreated) {
        toast({
          title: 'Free Plan Activated',
          description: 'Your free plan is ready. Add a payment method when you\'re ready to run comparisons or supplements.',
        });

        // Refresh subscription + status caches so the app unlocks immediately
        queryClient.invalidateQueries({ queryKey: [`/api/billing/subscriptions/company/${user?.companyId}`] });
        queryClient.invalidateQueries({ queryKey: ['subscription', 'status', user?.companyId] });
        void refetchSubscription();

        // During onboarding, send the user into the app
        if (redirectPath === 'onboarding') {
          window.location.href = '/';
        }
        return;
      }

      // Handle checkout session redirect (billing period change or initial subscription)
      if ('url' in data && data.url) {
        // Checkout session created: redirect to Stripe
        window.location.href = data.url;
        return;
      }

      if ('requiresPayment' in data && data.requiresPayment && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }

      // Handle successful subscription change (not checkout redirect)
      if ('requiresPayment' in data && !data.requiresPayment) {
        // Check if subscription was previously in canceled status
        const wasReactivated = subscription?.canceledAt !== null && subscription?.canceledAt !== undefined;

        let title: string;
        let description: string;

        if (wasReactivated) {
          // Subscription was reactivated
          title = 'Subscription Reactivated!';
          description = data.isDowngrade
            ? 'Your subscription has been reactivated and will change to the new plan at the end of your current billing period.'
            : 'Your subscription has been reactivated and upgraded immediately.';
        } else {
          // Normal tier change
          title = data.isDowngrade ? 'Downgrade Scheduled' : 'Upgrade Complete';
          description = data.isDowngrade
            ? 'Your plan will downgrade at the end of your current billing period.'
            : 'Your plan has been upgraded immediately.';
        }

        toast({
          title,
          description,
        });
        
        // Invalidate queries to mark data as stale
        queryClient.invalidateQueries({ queryKey: [`/api/billing/subscriptions/company/${user?.companyId}`] });
        
        // Poll for updated subscription data with exponential backoff
        const pollForUpdate = async (attempt = 1, maxAttempts = 5) => {
          if (attempt > maxAttempts) {
            return;
          }

          const { data: updatedSubscription } = await refetchSubscription();
          
          // Check if canceledAt field has been properly updated
          // (if subscription was reactivated, canceledAt should now be null)
          const isUpdated = wasReactivated 
            ? updatedSubscription?.canceledAt === null 
            : true; // For non-reactivation cases, first refetch is sufficient

          if (!isUpdated) {
            // Exponential backoff: 200ms, 400ms, 800ms, 1600ms, 3200ms
            const delay = 200 * Math.pow(2, attempt - 1);
            
            setTimeout(() => {
              void pollForUpdate(attempt + 1, maxAttempts);
            }, delay);
          }
        };

        // Start polling
        void pollForUpdate();
      }
      // If it's a checkout session, we'll redirect in mutationFn (no onSuccess needed)
    },
    onError: (error: unknown) => {
      logApiError(error, 'Subscription mutation');

      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      // Always clear the selected plan after mutation completes (success or error)
      // This prevents the button from staying in "Processing" state
      setSelectedPlan(null);
    },
  });

  const handleSelectPlan = (tierCode: TierCode) => {
    setSelectedPlan(tierCode);
    subscriptionMutation.mutate({ tierCode, billingPeriod: billingInterval });
  };

  const handleManageBilling = async () => {
    if (!user?.companyId) {
      toast({
        title: 'Error',
        description: 'No company associated with your account',
        variant: 'destructive',
      });
      return;
    }

    try {
      const baseUrl = window.location.origin;
      const result = await apiClient.billing.createBillingPortalSession({
        createBillingPortalSessionRequest: {
          returnUrl: `${baseUrl}/settings?tab=plans`,
        }
      });

      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error) {
      logApiError(error, 'Billing portal');

      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription?.id) {
      toast({
        title: 'Error',
        description: 'No active subscription found',
        variant: 'destructive',
      });
      return;
    }

    try {
      await apiClient.billing.cancelBillingSubscription({
        subscriptionId: subscription.id
      });

      toast({
        title: 'Subscription Canceled',
        description: `Your subscription will remain active until ${formatDate(subscription.renewsAt)}. You can reactivate it anytime before then.`,
      });

      // Refresh subscription data
      queryClient.invalidateQueries({ queryKey: [`/api/billing/subscriptions/company/${user?.companyId}`] });
    } catch (error) {
      logApiError(error, 'Cancel subscription');

      toast({
        title: 'Error',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const getCurrentTierPrice = (): number => {
    if (!subscription) return 0;
    const currentTier = tiers.find(t => t.code === subscription.tierCode);
    if (!currentTier) return 0;
    
    // Return price aligned with current billing period
    return subscription.billingPeriod === 'yearly' 
      ? currentTier.yearlyPriceCents 
      : currentTier.monthlyPriceCents;
  };

  // Check if user is admin or owner
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  return {
    // Data
    user,
    tiers,
    subscription,
    totalUsage,
    creditBalance,

    // Loading states
    tiersLoading,

    // Local state
    billingInterval,
    selectedPlan,

    // Permissions
    isAdmin,

    // Handlers
    setBillingInterval,
    handleSelectPlan,
    handleManageBilling,
    handleCancelSubscription,

    // Computed values
    getCurrentTierPrice,
    tierMetadata: TIER_METADATA,
  };
}
